import { useMemo, useState } from "react";

import HeaderTexts from "../../common/HeaderTexts";
import FixedCharges from "../price/FixedCharges";

import {
    formatHHMM,
    formatTimeTo12Hour,
    truncateText,
} from "../../../../utils";
import CardTokenizing from "../../../../cardTokenizing/CardTokenizing";

const FinalOrder = ({
    jobDescription,
    jobGeolocationDetails,
    skill,
    subSkill,
    range = [],
    calculateFutureTime,
    jobRatePreHour,
}) => {
    const hourlyRate = jobRatePreHour?.rate_per_hour_with_company_commission ?? 0;

    const [isOpenCardModal, setIsOpenCardModal] = useState(false);

    const toggleState = (setter) => () => setter((prev) => !prev);

    const timeRange = useMemo(() => {
        if (
            range.length < 2 ||
            typeof calculateFutureTime !== "function"
        ) {
            return "--:-- - --:--";
        }

        const formatRangeTime = (value) =>
            formatTimeTo12Hour(
                formatHHMM(calculateFutureTime(value))
            );

        return `${formatRangeTime(range[0])} - ${formatRangeTime(
            range[1]
        )}`;
    }, [range, calculateFutureTime]);

    const timelineItems = useMemo(
        () => [
            {
                icon: "location_on",
                title: "Location",
                value:
                    jobGeolocationDetails?.address ||
                    "No location selected",
            },
            {
                icon: "alarm",
                title: "Time",
                value: `Today, ${timeRange}`,
            },
            {
                icon: "build",
                title: "Service",
                value: `${skill?.skill_name || "-"} - ${
                    subSkill?.sub_skill_name || "-"
                }`,
            },
            {
                icon: "draft",
                title: "Note",
                value: truncateText(jobDescription || "-", 150),
            },
        ],
        [
            jobGeolocationDetails?.address,
            timeRange,
            skill?.skill_name,
            subSkill?.sub_skill_name,
            jobDescription,
        ]
    );

    return (
        <div 
            className="mx-3" 
            style={{
                height: "635px",
                overflow: "auto"
            }}
        >
            <HeaderTexts title="Final Order" subTitle="" />

            <div className="card card-plain">
                <div className="card-body px-0">
                    <div className="timeline timeline-one-side">
                        {timelineItems.map(
                            ({ icon, title, value }, index) => (
                                <div
                                    key={`${title}-${index}`}
                                    className="timeline-block mb-2"
                                >
                                    <span className="timeline-step">
                                        <i
                                            className="material-symbols-rounded text-dark text-gradient"
                                            style={{ fontSize: 20 }}
                                        >
                                            {icon}
                                        </i>
                                    </span>

                                    <div className="timeline-content">
                                        <h6 className="text-dark text-md font-weight-bold mb-0">
                                            {title}
                                        </h6>

                                        <p className="text-secondary font-weight-normal text-sm mb-0">
                                            {value}
                                        </p>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            <FixedCharges
                isHome
                isWithIcon={false}
                isShowOther
                hourlyRate={hourlyRate}
            />

            <div className="card card-plain mt-3">
                <div className="card-body">
                    <h6 className="mb-0 text-md">Pay Later</h6>

                    <ul className="text-muted ps-3 mb-0 float-start">
                        <li>
                            <span className="text-sm">
                                {hourlyRate}/hr on site (1hr minimum,
                                then 15-min increments).
                            </span>
                        </li>

                        <li>
                            <span className="text-sm">
                                You approve the final amount before
                                payment
                            </span>
                        </li>

                        <li>
                            <span className="text-sm">
                                If parts are needed, we'll always ask
                                first.
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="card card-plain">
                <div className="card-body">
                    <h6 className="mb-0 text-md">VAT & pricing clarity</h6>

                    <p className="text-sm text-muted mb-2">
                        All prices shown are final and VAT-inclusive — nothing will be added on top.
                    </p>

                    <p className="text-sm text-muted mb-0">
                       (For businesses) Instafix fees include VAT. VAT reclaimability on the tradesperson’s labour depends on their VAT status.
                    </p>
                </div>
            </div>

            <p className="text-xs text-muted mt-2">
                You only pay for the time on site once the work is done. Your Instafix pro will keep you updated throughout the visit.
            </p>

            <CardTokenizing show={isOpenCardModal} closeModal={toggleState(setIsOpenCardModal)}/>
        </div>
    );
};

export default FinalOrder;